import React, { useState, useEffect } from 'react';
import { getProjects } from '../../services/projectsService';
import { createMilestone, updateMilestone } from '../../services/milestonesService';

const MilestoneForm = ({ isOpen, onClose, milestone = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    due_date: '',
    end_date: '',
  });
  const [projects, setProjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getProjects().then((res) => {
        if (res.success && res.data) setProjects(res.data);
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (milestone) {
      setFormData({
        project_id: String(milestone.project_id),
        name: milestone.name || '',
        due_date: milestone.due_date ? milestone.due_date.slice(0, 10) : '',
        end_date: milestone.end_date ? milestone.end_date.slice(0, 10) : '',
      });
    } else {
      setFormData({
        project_id: '',
        name: '',
        due_date: '',
        end_date: '',
      });
    }
    setErrors({});
  }, [milestone, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.project_id) newErrors.project_id = 'Seleccione un proyecto';
    if (!formData.name?.trim()) newErrors.name = 'El nombre del hito es obligatorio';
    if (formData.name?.length > 255) newErrors.name = 'Máximo 255 caracteres';
    if (!formData.due_date) newErrors.due_date = 'La fecha de inicio es obligatoria';
    if (formData.end_date && formData.due_date && formData.end_date < formData.due_date) {
      newErrors.end_date = 'La fecha fin debe ser igual o posterior a la fecha de inicio';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        project_id: Number(formData.project_id),
        name: formData.name.trim(),
        due_date: formData.due_date,
        end_date: formData.end_date || null,
      };
      if (milestone) {
        await updateMilestone(milestone.id, payload);
      } else {
        await createMilestone(payload);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) setErrors(data.errors);
      else setErrors({ submit: data?.message || 'Error al guardar' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {milestone ? 'Editar Hito' : 'Nuevo Hito'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proyecto *</label>
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">Seleccione un proyecto</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} – {p.name}
                  </option>
                ))}
              </select>
              {errors.project_id && (
                <p className="mt-1 text-sm text-red-600">{errors.project_id}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del hito *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                placeholder="Ej. Entrega fase 1, Cierre de contrato"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio *</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              />
              {errors.due_date && <p className="mt-1 text-sm text-red-600">{errors.due_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
              />
              {errors.end_date && <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>}
            </div>
            {errors.submit && (
              <p className="text-sm text-red-600">{errors.submit}</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md disabled:opacity-50"
              >
                {loading ? 'Guardando...' : milestone ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MilestoneForm;
